<template>
  <div class="job-content f-c" v-loading="loadingData">
    <div class="header-buttons">
      <el-button
        type="primary"
        plain
        style="width: 140px;"
        size="mini"
        @click="toggleTableExpand = !toggleTableExpand"
        >{{ toggleTableExpand ? 'Show' : 'Hide' }} Group Detail
      </el-button>
    </div>
    <basic-table
      :table-data="pageData"
      :table-headers="UnloadingJobContentHeaders"
      :has-actions="hasActions"
      :has-row-expand="true"
      :action-width="200"
      rowKey="name"
      :checkExpand="() => !toggleTableExpand"
      :paginationChange="paginationChange"
      :showPagination="false"
      :summary="true"
    >
      <template v-slot:row-expand="props">
        <span class="hint-text">
          Scroll up and down to view tables of goods and pallets
        </span>
        <div class="goods-list f-c" style="gap: 10px;">
          <pallet-list
            v-if="showPalletList"
            :palletQuery="{ groupNameList: [props.row.name], jobIds: [jobId] }"
            :showActions="false"
            :showGroupName="false"
            :showHeaderOps="false"
          />
          <basic-table
            :table-data="getGroupGoods(props.row)"
            :table-headers="DisableSortingWrap(GoodsTableHeaders)"
            :has-actions="false"
            :showPagination="false"
            :has-row-expand="false"
          />
        </div>
        <div class="note-list f-r">
          <div
            v-for="(note, index) in props.row.notes"
            :key="'note-' + index"
            class="note f-c"
            :class="{
              important: note.status === NoteImportant,
              unimportant: note.status === NoteDeleted
            }"
          >
            <div class="note-content">
              <div class="time">
                {{ getNoteCreatedTime(note) }}
              </div>
              <div
                class="content"
                :class="{
                  striked: note.status === NoteDeleted
                }"
              >
                {{ note.content }}
              </div>
            </div>
            <div class="status-buts f-r">
              <svg-icon
                icon-class="important"
                @click.native="
                  () =>
                    changeStatus(
                      props.$index,
                      props.row,
                      index,
                      note,
                      NoteNormal
                    )
                "
                v-if="note.status === NoteImportant"
              />
              <svg-icon
                icon-class="unimportant"
                @click.native="
                  () =>
                    changeStatus(
                      props.$index,
                      props.row,
                      index,
                      note,
                      NoteImportant
                    )
                "
                v-if="note.status === NoteNormal"
              />
              <svg-icon
                icon-class="trashed"
                @click.native="
                  () =>
                    changeStatus(
                      props.$index,
                      props.row,
                      index,
                      note,
                      note.status === NoteDeleted ? NoteNormal : NoteDeleted
                    )
                "
                :class="{ deleted: note.status === NoteDeleted }"
              />
            </div>
          </div>
        </div>
      </template>
      <template v-slot:row-actions="props">
        <div class="actions f-r" style="flex-wrap: wrap;">
          <el-button
            size="small"
            type="text"
            @click="() => newGoodsGroupNote(props.index, props.row)"
          >
            New Note
          </el-button>
          <el-button
            size="small"
            type="text"
            class="success-text"
            @click="() => labelGroup(props.row)"
          >
            Labels
          </el-button>
          <el-button
            size="small"
            type="text"
            class="warning-text"
            @click="() => openExtraLabelsDialog(props.row)"
          >
            Extra Labels
          </el-button>
          <el-button
            size="small"
            type="text"
            class="danger-text"
            @click="openEditGoodsGroup(props.row)"
          >
            Modify Group
          </el-button>
        </div>
        <slot
          name="actions"
          v-bind:row="props.row"
          v-bind:index="props.index"
        />
      </template>
    </basic-table>
    <note-edit ref="newNote" />
    <goods-group-modify ref="goodsGroupModify" :job-id="jobId" v-if="jobId" />
    <common-dialog
      ref="extraLabelsDialog"
      title="Extra Labels"
      :inputs-map="morePalletForm"
      :save-cb="morePalletLabels"
    />
  </div>
</template>
<script>
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
// msg
import PagesMessage from '@/core/message/pages'
// api
import { queryGoods } from '@/api/goods'
import { labelGroupPallets, modifyGoodsGroup } from '@/api/job'
// models
import { GoodsTableHeaders } from '@/core/models/goods'
import { MorePalletForm, UnloadingJobContentHeaders } from '@/core/models/job'
import { JobStatus } from '@/core/models/constant'
import { NoteDeleted, NoteNormal, NoteImportant } from '@/core/models/note'
// components
import BasicTable from '@/components/common/BasicTable.vue'
import CommonDialog from '@/components/common/CommonDialog.vue'
import NoteEdit from '@/components/note/NoteNew.vue'
import GoodsGroupModify from '@/components/jobs/GoodsGroupModify.vue'
import PalletList from '@/components/pallet/PalletList.vue'
// utils
import { getNoteCreatedTime } from '@/core/utils/note'
import { viewPdf } from '@/core/utils'
import { DisableSortingWrap } from '@/core/configs/types'

export default {
  name: 'JobContent',
  components: {
    BasicTable,
    CommonDialog,
    NoteEdit,
    GoodsGroupModify,
    PalletList
  },
  props: {
    orderId: {
      type: String,
      required: false
    },
    jobId: {
      type: String,
      required: false
    },
    jobContent: {
      type: Array,
      required: true
    },
    hasActions: {
      type: Boolean,
      default: true
    },
    jobStatus: {
      type: String,
      required: true
    },
    showPalletList: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      UnloadingJobContentHeaders,
      GoodsTableHeaders,
      NoteDeleted,
      NoteNormal,
      NoteImportant,
      loadingData: false,
      morePalletForm: cloneDeep(MorePalletForm),
      rowClassName: {},
      goodsOfGroup: null,
      toggleTableExpand: true,
      DisableSortingWrap
    }
  },
  computed: {
    pageData() {
      return {
        records: this.loadingData ? [] : this.jobContent,
        size: this.jobContent.length,
        total: this.jobContent.length
      }
    }
  },
  watch: {
    orderId: {
      handler(newValue, oldValue) {
        if (isEqual(newValue, oldValue)) return
        this.getGoodsOfGroups()
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    refreshPage() {},
    sortChange(sort) {},
    paginationChange(key, path, value) {},
    getNoteCreatedTime,
    getGroupGoods(group) {
      const { name, goodsList } = group
      const a = this.goodsOfGroup?.[name] ?? goodsList ?? []
      return {
        records: a,
        size: a.length,
        total: a.length
      }
    },
    changeStatus(index, group, noteIndex, note, status) {
      const original = cloneDeep(group.notes)
      original[noteIndex].status = status
      modifyGoodsGroup(this.jobId, group.name, { notes: original }).then(
        res => {
          if (res) {
            // PagesMessage.emit(PagesMessage.eventMap.REFRESH_JOB_DETAIL)
            this.$set(group, 'notes', original)
          }
        }
      )
    },
    getRowClassName() {
      if (this.jobStatus === JobStatus.JobCompleted) {
        for (let i = 0; i < this.jobContent.length; i++) {
          const group = this.jobContent[i]
          if (group.quantity !== group.actualQuantity) {
            this.$set(this.rowClassName, i, 'error-row')
          }
        }
      }
    },
    async getGoodsOfGroups() {
      if (this.orderId) {
        this.loadingData = true
        const goodsPage = await queryGoods({
          orderId: this.orderId,
          size: -1
        })
        const goodsList = goodsPage?.records ?? []
        const goodsMap = Object.fromEntries(
          Object.values(this.jobContent).map(group => {
            const goods = goodsList.filter(goods =>
              group.goods.includes(goods.id)
            )
            return [group.name, goods]
          })
        )
        this.$set(this, 'goodsOfGroup', goodsMap)
        this.loadingData = false
      }
    },
    newGoodsGroupNote(index, group) {
      const groupName = group.name
      const saveNote = (newNote, done, error) => {
        modifyGoodsGroup(this.jobId, groupName, {
          notes: [...(group.notes ?? []), newNote]
        })
          .then(res => {
            if (res) {
              // add note into it
              if (!group.notes) this.$set(group, 'notes', [])
              group.notes.push(newNote)
            }
            done()
          })
          .catch(err => {
            error(err)
          })
      }
      this.$refs.newNote.open(saveNote)
    },
    openExtraLabelsDialog(group) {
      this.morePalletForm[0].value = group.name
      this.morePalletForm[1].value = group.palletCount
      this.$refs.extraLabelsDialog.open()
    },
    morePalletLabels(inputs, done, error) {
      modifyGoodsGroup(this.jobId, this.morePalletForm[0].value, {
        palletCount:
          this.morePalletForm[2].value + this.morePalletForm[1].value,
        appendToShipment: inputs.appendToShipment,
        name: this.morePalletForm[0].value
      })
        .then(res => {
          PagesMessage.emit(PagesMessage.eventMap.REFRESH_JOB_DETAIL)
          const extraLabels = res?.moreLabels ?? []
          if (extraLabels.length > 0) {
            labelGroupPallets(
              this.jobId,
              this.morePalletForm[0].value,
              this.morePalletForm[1].value,
              -1
            ).then(res => {
              viewPdf(res)
            })
          }
          done()
        })
        .catch(err => {
          error(err)
        })
    },
    async labelGroup(row) {
      const blob = await labelGroupPallets(this.jobId, row.name, 0, -1)
      viewPdf(blob)
    },
    openEditGoodsGroup(group) {
      this.$refs.goodsGroupModify.open(group)
    }
  },
  mounted() {
    this.getRowClassName()
    PagesMessage.on(
      PagesMessage.eventMap.REFRESH_JOB_CONTENT,
      this.getRowClassName
    )
  }
}
</script>
<style scoped lang="scss">
@import '@/assets/styles/common.scss';
@import '@/assets/styles/note-list.scss';

.skus {
  padding-left: 10px;
  padding-right: 10px;
  flex-wrap: wrap;
  font-size: $regular-body-font-size;
  gap: 5px 10px;

  @extend .f-r;
}

.actions {
  width: 100%;

  .el-button {
    margin: 0 5px !important;
  }
}

.hint-text {
  color: $deep-warning;
  padding: 5px 10px;

  @extend .bold-oswald;
}

.goods-list {
  margin: 0 10px;
  margin-bottom: 10px;
  margin-right: 210px;
  max-height: 500px !important;
  overflow-y: scroll;

  ::-webkit-scrollbar {
    appearance: none;
    width: 7px;
  }

  ::v-deep thead tr {
    height: 20px !important;

    th {
      background-color: rgba($business-color, 0.1) !important;

      .cell {
        height: 20px !important;
      }
    }
  }

  ::v-deep .el-table__row {
    height: 18px !important;
    font-size: 14px !important;
  }
}

.header-buttons {
  margin-bottom: 10px;
}
</style>
